<template>
  <ul>
    <li v-if="showDebug">
      <div v-for="(item, key) in viewColumn" :key="key">
        <span style="font-weight: 500">{{ key }}:</span> {{ item }}
      </div>
    </li>
    <!--SORT UP-->
    <li @click="tableStore.setSorting({ columnName: viewColumn.field, order: 'ASC' })">
      <font-awesome-icon :icon="['fal', 'arrow-down-a-z']" class="menuitem-icon" />
      <span>Sorter stigende</span>
    </li>
    <!--SORT DOWN-->
    <li @click="tableStore.setSorting({ columnName: viewColumn.field, order: 'DESC' })">
      <font-awesome-icon :icon="['fal', 'arrow-down-z-a']" class="menuitem-icon" />
      <span>Sorter synkende</span>
    </li>
    <li class="separator" />
    <!--INPUT-->
    <li>
      <div v-if="useNewHeaderExperience">
        <!-- Søkefelt -->
        <BaseInput
          v-model="searchTerm"
          placeholder="Søk etter verdi..."
          type="text"
          @input="onSearchInput"
        />

        <!-- Resultat liste (10-12 linjer, scroll, multi-select) -->
        <div class="results-list-container">
          <div v-if="isLoading" class="loading-indicator">Laster...</div>
          <div v-else-if="serverOptions.length === 0" class="no-results">Ingen treff</div>
          <div v-else class="results-list">
            <div
              v-for="opt in serverOptions"
              :key="opt.value"
              :class="{ selected: isSelected(opt.value) }"
              class="result-item"
              @click="toggleSelection(opt)"
            >
              <input :checked="isSelected(opt.value)" type="checkbox" @click.stop="toggleSelection(opt)"/>
              <span class="result-label">{{ opt.label }}</span>
            </div>
          </div>
          <div v-if="hasMore" class="pagination-info">
            <span>Flere resultater tilgjengelig - fortsett å søke for å begrense</span>
          </div>
        </div>

        <!-- Handlinger -->
        <div class="actions-row">
          <button class="action-btn-full secondary" @click="clearLocalSelection">
            <font-awesome-icon :icon="['fal', 'eraser']"/>
            Nullstill søk
          </button>
        </div>
      </div>
      <div v-else>
        <BaseSelect v-model="selectedValue" :options="valueListItems" @change="addInput"/>
      </div>
    </li>
    <!--LIST OF FILTERS (kun vis hvis det er valgte items i ny opplevelse)-->
    <li v-if="useNewHeaderExperience && inputValue.length > 0" class="selected-items-section">
      <div class="selected-items-header">Valgte verdier:</div>
      <div v-for="(filter, idx) in inputValue" :key="idx" class="selected-item">
        <span class="filter-item">{{ filter.itemText }}</span>
        <font-awesome-icon :icon="['fal', 'times']" class="remove-icon" @click.stop="removeInput(idx)"/>
      </div>
    </li>
    <!--LIST OF FILTERS (v1)-->
    <li v-for="(filter, idx) in inputValue" v-else-if="!useNewHeaderExperience" :key="idx" class="p-menuitem-link" style="padding-left: 40px; width: 100%">
      <span class="filter-item ml-2" style="">
        {{ filter.itemText }}
      </span>
      <font-awesome-icon :icon="['fal', 'times']" style="margin-right: 1rem; margin-left: 6px" @click.stop="removeInput(idx)" />
    </li>
    <!--REMOVE FILTERS-->
    <li @click="tableStore.clearFilter(viewColumn.field)">
      <font-awesome-icon :icon="['fal', 'filter-slash']" class="menuitem-icon remove" />
      <span>Fjern alle filter</span>
    </li>
    <li class="separator" />
    <!--FILTER NULL-->
    <li @click="tableStore.addFilterNull(viewColumn.field)">
      <span style="padding-left: 28px">Filter <strong>p&aring;</strong> blanke</span>
    </li>
    <!--FILTER NOT NULL-->
    <li @click="tableStore.addFilterNotNull(viewColumn.field)">
      <span style="padding-left: 28px">Filter <strong>bort</strong> blanke</span>
    </li>
    <li class="separator" />
    <!--REFRESH-->
    <li @click="onRefresh">
      <font-awesome-icon :icon="['fal', 'play']" class="menuitem-icon play" />
      <span>Oppdater</span>
    </li>
  </ul>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useTableStore } from '@/store/storeTable'
import { useGlobalStore } from '@/store/storeGlobal'
import { useVerdilisteStore } from '@/store/storeVerdiliste'
import { useUserPreferencesStore } from '@/store/storeUserPreferences'
import { axiosClient } from '@/helpers/auth'
import BaseSelect from '@/components/BaseComponents/BaseSelect.vue'
import BaseInput from '@/components/BaseComponents/BaseInput.vue'

const props = defineProps({
  viewColumn: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close'])

// GlobalStore
const globalStore = useGlobalStore()
const { showDebug } = storeToRefs(globalStore)

// TableStore
const tableStore = useTableStore()
const { whereItems, orderByItems } = storeToRefs(tableStore)

// VerdilisteStore
const verdilisteStore = useVerdilisteStore();

// User Preferences (feature toggle)
const userPreferencesStore = useUserPreferencesStore();
const useNewHeaderExperience = computed(() => {
  const up = userPreferencesStore.userPreferences;
  // Prøver både toppnivå og DashboardConfig for robusthet
  return !!(up?.useNewHeaderExperience || up?.dashboardConfig?.useNewHeaderExperience);
})

const inputValue = ref([]);
const selectedValue = ref(null);

// v1 (lokal verdiliste)
const valueListItems = ref({});

// Nullstill valg når kolonne endres (kontekst-håndtering)
watch(() => props.viewColumn.field, () => {
  inputValue.value = [];
  selectedValue.value = null;
  searchTerm.value = '';
  if (useNewHeaderExperience.value) {
    fetchServerOptions('');
  }
}, { immediate: false });

watch(() => props.viewColumn.listOfValueObjectId, (listOfValueObjectId) => {
  if (!useNewHeaderExperience.value) {
    valueListItems.value = verdilisteStore.getVerdiListe(listOfValueObjectId);
  }
}, { immediate: true });

// v2 (server-side type-ahead)
const searchTerm = ref('')
const isLoading = ref(false)
const serverOptions = ref([])
const hasMore = ref(false)
let abortController = null
let debounceTimer = null

function buildPublicQueryRequest () {
  return {
    databaseObjectId: tableStore.query?.head?.databaseObjectId || props.viewColumn?.databaseObjectId,
    wheres: whereItems.value || [],
    orderBys: orderByItems.value || [],
    page: 1,
    pageSize: 0,
    useOrderBy: false
  }
}

async function fetchServerOptions (term) {
  if (!useNewHeaderExperience.value) return
  if (abortController) {
    try {
      abortController.abort()
    } catch (err) {
      // Ignore abort errors
      console.debug('Abort error:', err)
    }
  }
  abortController = new window.AbortController()
  isLoading.value = true
  try {
    const params = { column: props.viewColumn.field, q: term || '', take: 50 }
    const body = buildPublicQueryRequest()
    const res = await axiosClient.post('/v2/query/values', body, { params, signal: abortController.signal })
    const items = res?.data?.items || []
    hasMore.value = res?.data?.hasMore || false
    serverOptions.value = items.map(kv => ({ value: kv.key ?? kv.value ?? kv, label: kv.value ?? kv.key ?? kv }))
  } catch (err) {
    if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
      console.error('fetchServerOptions error', err)
    }
  } finally {
    isLoading.value = false
    abortController = null
  }
}

function onSearchInput () {
  if (!useNewHeaderExperience.value) return
  if (debounceTimer) {
    window.clearTimeout(debounceTimer)
  }
  debounceTimer = window.setTimeout(() => fetchServerOptions(searchTerm.value), 300)
}

onMounted(() => {
  if (useNewHeaderExperience.value) {
    fetchServerOptions('')
  }
})

// Multi-select logic
function isSelected (value) {
  return inputValue.value.some(item => item.itemValue === value)
}

function toggleSelection (opt) {
  const idx = inputValue.value.findIndex(item => item.itemValue === opt.value)
  if (idx >= 0) {
    // Deselect
    inputValue.value.splice(idx, 1)
  } else {
    // Select
    inputValue.value.push({ itemText: opt.label, itemValue: opt.value })
  }
  updateFilter()
}

function addInput () {
  // Fallback for v1 (dropdown)
  const list = useNewHeaderExperience.value ? serverOptions.value : valueListItems.value
  const item = list.find(x => x.value === selectedValue.value)
  if (item) {
    inputValue.value.push({ itemText: item.label, itemValue: item.value })
    selectedValue.value = null;
    updateFilter()
  }
}

function removeInput (index) {
  inputValue.value.splice(index, 1)
  updateFilter()
}

function updateFilter () {
  if (inputValue.value.length === 0) {
    // Ingen valg = fjern filter
    tableStore.clearFilter(props.viewColumn.field)
  } else {
    // IN-filter med array av verdier
    const inputItem = inputValue.value.map(item => item.itemValue)
    tableStore.addFilterActionOperator(props.viewColumn.field, 'in', inputItem)
  }
}

function onRefresh () {
  // Hvis bruker har søkestreng men ingen seleksjon: kjør LIKE
  if (useNewHeaderExperience.value && searchTerm.value && inputValue.value.length === 0) {
    const whereItem = tableStore.parseQueryOperator(searchTerm.value, props.viewColumn.field)
    tableStore.addFilter(whereItem)
  }
  tableStore.refreshQuery();
  // lukk meny
  emit('close')
}

function clearLocalSelection () {
  // Nullstill kun lokal UI-state (søk og valg), ikke filteret
  searchTerm.value = ''
  selectedValue.value = null
  inputValue.value = []
  serverOptions.value = []
  if (useNewHeaderExperience.value) {
    fetchServerOptions('')
  }
}

</script>

<style scoped>
ul {
  list-style-type: none;
  padding-inline-start: 0;
  margin: 0;
}

li {
  cursor: pointer;
  padding: 12px;
}

li:hover {
  background-color: rgb(241, 241, 241);
}

.separator {
  border-top: 1px solid silver;
  padding: 0;
}

.menuitem-icon {
  margin-right: 12px;
}

.menu {
  width: calc(100% - 6px) !important;
  font-weight: bold;
}

.play {
  color: #689f38;
}

.remove {
  color: #d32f2f;
}

.filter-item {
  display: inline-block;
  width: 11.6rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Ny resultat-liste design */
.results-list-container {
  margin-top: 8px;
  margin-bottom: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.results-list {
  max-height: 240px; /* ~10-12 linjer @ 20-24px/linje */
  overflow-y: auto;
}

.result-item {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.result-item:hover {
  background-color: #f5f5f5;
}

.result-item.selected {
  background-color: #e3f2fd;
}

.result-item input[type="checkbox"] {
  margin-right: 8px;
  cursor: pointer;
}

.result-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loading-indicator, .no-results {
  padding: 16px;
  text-align: center;
  color: #999;
  font-style: italic;
}

.pagination-info {
  padding: 8px;
  font-size: 12px;
  color: #666;
  border-top: 1px solid #eee;
  background: #fafafa;
}

.actions-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.action-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #f5f5f5;
  border-color: #bbb;
}

.action-btn.secondary {
  color: #555;
}

.action-btn-full {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.action-btn-full:hover {
  background: #f5f5f5;
  border-color: #bbb;
}

.action-btn-full.secondary {
  color: #555;
}

/* Valgte verdier seksjon */
.selected-items-section {
  padding: 8px 12px;
  background: #f9f9f9;
  border-top: 1px solid #eee;
}

.selected-items-header {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 6px;
}

.selected-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  margin-bottom: 4px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 3px;
}

.selected-item .filter-item {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-item .remove-icon {
  color: #d32f2f;
  cursor: pointer;
  margin-left: 8px;
}

.selected-item .remove-icon:hover {
  color: #b71c1c;
}
</style>
